import { Button, Col, Slider, Text, useTheme } from "@rally/ui-kit";
import { useEffect, useRef, useState } from "react";
import { say } from "../localization/say.ts";
import { availabilityUrl, clock, type DvrWindow, dvrWindow, waitForManifest } from "./recording-dvr.ts";

export type CallRecordingRead = {
  kind: "recording";
  status: "starting" | "active" | "ending" | "complete" | "failed" | "aborted" | "limit_reached";
  playlist: string;
  livePlaylist: string;
  startedAt: string | null;
  endedAt: string | null;
};

type HlsPlayer = {
  loadSource: (source: string) => void;
  attachMedia: (media: HTMLVideoElement) => void;
  destroy: () => void;
};

type Readiness = "preparing" | "ready" | "timed-out";

function useManifestReadiness(source: string | null): Readiness {
  const [readiness, setReadiness] = useState<Readiness>("preparing");
  useEffect(() => {
    setReadiness("preparing");
    if (!source) return;
    const controller = new AbortController();
    const wait = (milliseconds: number) =>
      new Promise<void>((resolve, reject) => {
        const timer = setTimeout(resolve, milliseconds);
        controller.signal.addEventListener(
          "abort",
          () => {
            clearTimeout(timer);
            reject(controller.signal.reason);
          },
          { once: true },
        );
      });
    void waitForManifest(async () => {
      try {
        const answer = await fetch(availabilityUrl(source), {
          credentials: "same-origin",
          cache: "no-store",
          signal: controller.signal,
        });
        return answer.status === 200;
      } catch (cause) {
        if (controller.signal.aborted) throw cause;
        return false;
      }
    }, wait)
      .then((ready) => {
        if (!controller.signal.aborted) setReadiness(ready ? "ready" : "timed-out");
      })
      .catch(() => undefined);
    return () => controller.abort();
  }, [source]);
  return readiness;
}

function useHls(video: React.RefObject<HTMLVideoElement | null>, source: string | null, startLive: boolean) {
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    const element = video.current;
    if (!element || !source) return;
    let dropped = false;
    let player: HlsPlayer | null = null;
    let positioned = false;
    const ready = () => {
      if (!positioned && element.seekable.length > 0) {
        positioned = true;
        const edge = startLive
          ? element.seekable.end(element.seekable.length - 1) - 0.5
          : element.seekable.start(0);
        element.currentTime = Math.max(0, edge);
      }
      setError(null);
    };
    element.addEventListener("loadedmetadata", ready);
    element.addEventListener("durationchange", ready);
    if (element.canPlayType("application/vnd.apple.mpegurl")) {
      element.src = source;
      element.load();
    } else {
      void import("hls.js")
        .then((kit) => {
          if (dropped) return;
          if (!kit.default.isSupported()) {
            setError(say("recording.playbackUnsupported"));
            return;
          }
          player = new kit.default({ startPosition: startLive ? -1 : 0 });
          player.loadSource(source);
          player.attachMedia(element);
        })
        .catch(() => setError(say("recording.playbackFailed")));
    }
    return () => {
      dropped = true;
      element.removeEventListener("loadedmetadata", ready);
      element.removeEventListener("durationchange", ready);
      player?.destroy();
      element.removeAttribute("src");
      element.load();
    };
  }, [source, startLive, video]);
  return error;
}

function LiveTimeline({ video }: { video: React.RefObject<HTMLVideoElement | null> }) {
  const theme = useTheme();
  const [window, setWindow] = useState<DvrWindow | null>(null);
  useEffect(() => {
    const element = video.current;
    if (!element) return;
    const update = () => setWindow(dvrWindow(element.seekable, element.currentTime));
    for (const event of ["loadedmetadata", "durationchange", "progress", "timeupdate"]) {
      element.addEventListener(event, update);
    }
    update();
    return () => {
      for (const event of ["loadedmetadata", "durationchange", "progress", "timeupdate"]) {
        element.removeEventListener(event, update);
      }
    };
  }, [video]);

  if (!window) {
    return (
      <Text typeName="caption" color="text.tertiary" testID="call-recording-timeline-preparing">
        {say("recording.preparing")}
      </Text>
    );
  }
  const scrub = (time: number) => {
    if (!video.current) return;
    video.current.currentTime = time;
    setWindow(dvrWindow(video.current.seekable, time));
  };
  const jump = () => {
    scrub(window.end - 0.5);
    void video.current?.play().catch(() => undefined);
  };

  return (
    <Col style={{ gap: theme.space[1] }} testID="call-recording-timeline">
      <Slider
        min={window.start}
        max={window.end}
        step={0.25}
        value={window.current}
        onChange={scrub}
        label={say("recording.timeline")}
      />
      <Col
        style={{
          flexDirection: "row",
          alignItems: "center",
          justifyContent: "space-between",
          gap: theme.space[2],
        }}
      >
        <Text typeName="caption" color="text.tertiary" testID="call-recording-elapsed">
          {clock(window.current - window.start)} / {clock(window.end - window.start)}
        </Text>
        {window.atLive ? (
          <Text typeName="caption" color="accent" testID="call-recording-at-live">
            {say("recording.atLive")}
          </Text>
        ) : (
          <Col style={{ flexDirection: "row", alignItems: "center", gap: theme.space[2] }}>
            <Text typeName="caption" color="text.tertiary" testID="call-recording-behind-live">
              {say("recording.behindLive")(clock(window.behind))}
            </Text>
            <Button size="sm" variant="soft" onPress={jump} testID="call-recording-live">
              {say("recording.jumpLive")}
            </Button>
          </Col>
        )}
      </Col>
      <Col style={{ flexDirection: "row", justifyContent: "space-between" }}>
        <Text typeName="caption" color="text.tertiary">
          0:00
        </Text>
        <Text typeName="caption" color="text.tertiary">
          {say("recording.liveEdge")}
        </Text>
      </Col>
    </Col>
  );
}

export function CallRecording({ recording, live }: { recording: CallRecordingRead; live: boolean }) {
  const theme = useTheme();
  const video = useRef<HTMLVideoElement | null>(null);
  const playable = ["active", "ending", "complete", "limit_reached"].includes(recording.status);
  const failed = recording.status === "failed" || recording.status === "aborted";
  const source = playable ? (live ? recording.livePlaylist : recording.playlist) : null;
  const readiness = useManifestReadiness(source);
  const error = useHls(video, readiness === "ready" ? source : null, live);

  if (!playable || readiness !== "ready") {
    return (
      <Text
        typeName="caption"
        color={failed || readiness === "timed-out" ? "danger" : "text.tertiary"}
        testID="call-recording-status"
      >
        {failed || readiness === "timed-out" ? say("recording.playbackFailed") : say("recording.preparing")}
      </Text>
    );
  }

  return (
    <Col style={{ gap: theme.space[2] }} testID="call-recording-player">
      <video
        ref={video}
        controls
        playsInline
        preload="metadata"
        aria-label={say("recording.player")}
        style={{ width: "100%", maxHeight: 420, borderRadius: theme.radius.md, backgroundColor: "black" }}
      />
      {live ? <LiveTimeline video={video} /> : null}
      {error ? (
        <Text typeName="caption" color="danger" testID="call-recording-playback-error">
          {error}
        </Text>
      ) : null}
    </Col>
  );
}
